import { useEffect, useState } from "react";
import Markdown from "react-markdown";
import { Grid, Typography, Button, useTheme, useMediaQuery } from "@mui/material";
import { useAuthDispatch } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import LoadingContent from "../../components/progress/LoadingContent";


export default function Terms() {
    const [termsContent, setTermsContent] = useState("");
    const [scrolledToBottom, setScrolledToBottom] = useState(false);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
    const authDispatch = useAuthDispatch();
    const navigate = useNavigate();

    // useEffect to get the termsContent from the public file
    useEffect(() => {
        if (!termsContent) {
            fetch(`${process.env.PUBLIC_URL}/terms.md`)
            .then((response) => response.text())
            .then((data) => setTermsContent(data));
        }
    }, [termsContent, setTermsContent]);

    // useEffect to check if the user has scrolled to the bottom of the terms-section
    useEffect(() => {
        const termsSection = document.querySelector(".terms-section");
        termsSection.addEventListener("scroll", () => {
            if (termsSection.scrollTop + termsSection.clientHeight >= termsSection.scrollHeight - termsSection.clientHeight) {
                setScrolledToBottom(true);
            }else{
                setScrolledToBottom(false);
            }
        });
    }, [setScrolledToBottom]);

    const handleAccept = async() => {
        setLoading(true);
        const { error, termsAcceptedOn } = await submitTerms();

        if(!error){
          setLoading(false);
          authDispatch({
            type: "updateAuth",
            payload: { termsAcceptedOn },
          });
          navigate("/", { replace: true });
        }else{
            alert("Error accepting terms")
        }
    };

    // a function to scroll to the bottom of the terms section
    const scrollToBottom = () => {
        const termsSection = document.querySelector(".terms-section");
        termsSection.scrollTop = termsSection.scrollHeight;
        setScrolledToBottom(true)
    };

    return (
        <Grid container direction = {isMediumScreen ? "column" : "row"} sx={{ justifyContent: "space-between", height: "100%", backgroundColor: theme.palette.white.main, }}>
            <Grid item sx={{ 
                backgroundColor: theme.palette.white.main,
                height: "100%",
                width: isMediumScreen ? "100%" : "50%", 
                padding: `67px 125px 83px 83px`,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
            }}>
                {/* <HeaderWithBackandLogout /> */}
                <Grid item id="left-area" 
                    sx={{ 
                        backgroundColor: theme.palette.white.main,
                        height: "calc(100% - 47px)",
                        width: "100%", 
                        marginBottom: "16px"
                    }}
                >
                    <Grid container direction='column' 
                        justifyContent="stretch"
                        alignItems="flex-start"
                        flexWrap="nowrap"
                        className="terms-section"
                        data-testid="terms-section"
                        sx={{ 
                            height: "100%", 
                            width: "100%", 
                            overflowY: "scroll",
                            direction: 'rtl', // Set the direction to right-to-left
                            position: 'relative', // Ensure proper stacking of scrollbar and content
                            '& .scroll-content': {
                                direction: 'ltr', // Set the direction of content to left-to-right
                            },
                            '&::-webkit-scrollbar': {
                                left: 0,
                                width: '8px',
                                height: "49px",
                            },
                            '&::-webkit-scrollbar-track': {
                                background: '#FFFFFF',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                width: '8px',
                                height: "49px",
                                background: "#C6CCD2 0% 0% no-repeat padding-box",
                                borderRadius: "8px",
                                opacity: "1"
                            },
                            }}>
                        <Grid className="scroll-content" sx = {{marginLeft: "10px"}}>
                        <Grid item sx = {{marginBottom: "23px"}}>
                                <Typography sx={{
                                    color: "var(--Primary-500, #16124C)",
                                    fontFamily: "Lato",
                                    fontSize: "25px",
                                    fontStyle: "normal",
                                    fontWeight: "900",
                                    lineHeight: "normal",
                                    alignSelf: "stretch"
                                }}>Terms of <span style={{ color: "var(--Secondary-500, #AC5CCC)"}}>Use</span></Typography>
                            </Grid>
                            <Grid item className="terms-content" sx={{ 
                                flewGrow: 1,
                            }}>
                                <Markdown components={{
                                    h1: ({ children }) => <Typography sx={{ 
                                        paddingBottom: "16px",
                                        paddingTop: "16px",
                                        color: "var(--Secondary-500, #AC5CCC)",
                                        fontFamily: "Lato",
                                        fontSize: "18px",
                                        fontStyle: "normal",
                                        fontWeight: "500",
                                        lineHeight: "normal",
                                    }}>{children}</Typography>,
                                    p: ({ children }) => <Typography sx={{ 
                                        color: "#A4A4A4",
                                        fontFamily: "Lato",
                                        fontSize: "14px",
                                        fontStyle: "normal",
                                        fontWeight: "400",
                                        lineHeight: "normal",
                                        alignSelf: "stretch"
                                    }}>{children}</Typography>,
                                }}>{termsContent}</Markdown>
                            </Grid> 
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item 
                    className="button-area" 
                    sx={{  
                        width: "100%",
                        height: "85px"
                    }}
                >
                    {
                        scrolledToBottom ? 
                        <Grid
                            container
                            direction="row"
                            sx = {{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "nowrap",
                                width: "100%",
                                height: "100%",
                            }}
                        >
                            <Button 
                                variant="outlined" 
                                color="white"
                                onClick={handleDeny}
                                className="deny-button"
                                sx={{
                                    width: `calc((100% - 8px) / 2)`,
                                    height: "60px",
                                    border: `1px solid ${isMediumScreen ? "#6F8090" : "#FFFFFF"}`,
                                    borderRadius: "6px",
                                    alignSelf: "center",
                                    '&:hover': {
                                        backgroundColor: "#F5F5F5", // Light gray background on hover
                                        borderColor: "#6F8090", // Change border color on hover
                                    },
                                }}>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        color: "var(--Neutrals-700, #C6CCD2)",
                                        textAlign: "center",
                                        fontFamily: "Lato",
                                        fontSize: "14px",
                                        fontStyle: "normal",
                                        fontWeight: "700",
                                        lineHeight: "normal",
                                        transform: "none",
                                        textTransform: "none",
                                    }}>
                                    Deny
                                </Typography>
                            </Button>
                            <Button 
                                variant="contained" 
                                onClick={handleAccept}
                                className="accept-button"
                                sx={{
                                    width: `calc((100% - 8px) / 2)`,
                                    height: "60px",
                                    alignSelf: "center",
                                    background: `#69B01C 0% 0% no-repeat padding-box !important`,
                                    borderRadius: "6px",
                                    opacity: "1"
                                }}>
                                {loading ? <LoadingContent iconStyle = {{color: "white", width: "17px !important", height: "17px !important"}}/> : 
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        color: "var(--Neutrals-100, #FFF)",
                                        textAlign: "center",
                                        fontFamily: "Lato",
                                        fontSize: "14px",
                                        fontStyle: "normal",
                                        fontWeight: "700",
                                        lineHeight: "normal",
                                        textTransform: "none",
                                    }}>
                                    Accept
                                </Typography>}
                            </Button>
                        </Grid> :
                        <Grid
                            container
                            direction="column"
                            sx = {{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                flexWrap: "nowrap",
                                width: "100%",
                                height: "100%",
                            }}
                        >
                            <Typography 
                                variant="body1"
                                onClick={scrollToBottom}
                                sx={{ 
                                    cursor: "pointer",
                                    color: "var(--Primary-500, #16124C)",
                                    fontFamily: "Lato",
                                    fontSize: "14px",
                                    fontStyle: "normal",
                                    fontWeight: "400",
                                    lineHeight: "normal",
                                }}>
                                Scroll down to <span style={{ color: "var(--Tertiary-500, #00A6FF)" }}>Accept</span>
                            </Typography>
                            <Button 
                                variant="contained" 
                                disabled={true}
                                onClick={handleAccept}
                                className="accept-button"
                                sx={{
                                    width: "100%",
                                    height: "60px",
                                    marginTop: "8px",
                                    alignSelf: "center",
                                    background: `#C6CCD2 0% 0% no-repeat padding-box !important`,
                                    borderRadius: "6px",
                                    opacity: "1",
                                }}>
                                {loading ? <LoadingContent iconStyle = {{color: "white", width: "17px !important", height: "17px !important"}}/> : 
                                <Typography 
                                    sx={{ 
                                        color: "var(--Neutrals-100, #FFF)",
                                        textAlign: "center",
                                        fontFamily: "Lato",
                                        fontSize: "14px",
                                        fontStyle: "normal",
                                        fontWeight: "700",
                                        lineHeight: "normal",
                                        textTransform: "none",
                                    }}>
                                    Accept
                                </Typography>}
                            </Button>
                        </Grid>
                    }
                </Grid>
            </Grid>   
            <Grid 
                className="right-area"
                sx={{ 
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: isMediumScreen ? "flex-start" : "flex-end",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    height: isMediumScreen ? "97px" : "100%",
                    width: isMediumScreen ? "100%" : "50%",
                    backgroundSize: !isMediumScreen && "cover",
                    backgroundPosition: !isMediumScreen && "center",
                    display: { xs: "none", md: "block" },
                }}
            >
                <img
                    src="/images/Product_registration-01.png"
                    alt="TC image"
                    className="TC-image"
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        aspectRatio: 13/15,
                        flexShrink: 0,
                        borderRadius: "6px", // Added borderRadius
                    }}
                />
            </Grid>
        </Grid>
    );
};

const handleDeny = () => {
    // redirect the user to the logout page
    window.location.href = `${process.env.REACT_APP_URL}/logout`;
};

// a function to submit terms to the backend
const submitTerms = async () => {
    // make POST request to /users/terms
    let response = await fetch('/api/users/terms', {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
    })
    let data = await response.json();
    if (!response.ok) {
        return { error: data.message };
    }
    return data;
}